<template>
  <div :class="wrapperClasses" class="e-overall">
    <transition name="canvas">
      <div v-show="!offcanvasOpen" class="e-canvas">
        <div v-if="!userInfo || !userInfo.id" class="e-canvas__background" />

        <TheHeader />

        <div class="e-content">
          <TheSidebar />
          <div class="e-scroll-container">
            <VuePerfectScrollbar class="e-content__inner">
              <div
                class="e-module e-module--limited e-module--left z-1"
              >
                <div style="display: flex; flex-direction: column; gap: 1.5rem;">
                  <div v-if="abandonedChatMessageOpen" class="e-abandoned-message">
                    <span class="e-abandoned-message__icon"><IconWarningTriangle /></span>
                    <span class="e-abandoned-message__text" v-text="kidsAbandonedInfoText"/>
                  </div>
                  <div class="e-kacheln">
                    <article class="e-kacheln__column e-kacheln__column_mail">
                      <div class="e-kacheln__icon">
                        <icon-mailsupport
                          :aria-label="$t('aria.icon.mailSupport')"
                          role="presentation"
                        />
                      </div>
                      <div class="e-kacheln__header">
                        <h1 class="e-kacheln__title">Mail-Beratung</h1>
                      </div>
                      <div class="e-kacheln__content" v-html="mailText" />
                      <footer class="e-kacheln__footer">
                        <router-link
                          :to="{ name: 'dialogs' }"
                          class="e-btn e-btn--secondary"
                        >
                        Zur Mail-Beratung
                        </router-link>
                      </footer>
                    </article>
                    <article
                      :class="{
                        'e-chat--offline': !chatIsOpen
                      }"
                      class="e-kacheln__column e-kacheln__column_chat"
                    >
                      <div class="e-kacheln__icon">
                        <icon-chat
                          :aria-label="$t('aria.icon.chat')"
                          role="presentation"
                        />
                      </div>
                      <div class="e-kacheln__header">
                        <h1 class="e-kacheln__title">Live-Chat</h1>
                      </div>
                      <div class="e-kacheln__content">
                        <div v-html="chatText" />
                        <table v-if="!chatIsOpen && upcomingChatEvents.length">
                          <tbody>
                            <tr
                              v-for="chatEvent in upcomingChatEvents"
                              :key="chatEvent.id"
                            >
                              <td valign="top">
                                {{
                                  format(chatEvent.beginning, 'EEEEEE, dd.MM.')
                                }}
                                {{ format(chatEvent.beginning, 'HH:mm') }}-{{
                                  format(chatEvent.end, 'HH:mm')
                                }}
                                Uhr | Thema: {{ chatEvent.subject }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <footer class="e-kacheln__footer">
                        <div class="link">
                          <button
                            v-if="!chatIsOpen"
                            :aria-disabled="!chatIsOpen"
                            :disabled="!chatIsOpen"
                            class="e-btn is-disabled"
                          >
                            zum Live-Chat
                          </button>
                          <router-link
                            v-else
                            :to="{ name: 'liveChat' }"
                            class="e-btn e-btn--secondary"
                          >
                            zum Live-Chat
                          </router-link>
                        </div>
                        <!-- <div class="chat-archive-link">
                        <router-link
                          v-if="userInfo && userInfo.chatCount>0"
                          :to="{name: 'chatDialogs'}"
                          class=""
                        >
                          Meine archivierten Chats
                        </router-link>
                      </div> -->
                      </footer>
                    </article>
                  </div>
                </div>
              </div>
            </VuePerfectScrollbar>
          </div>
        </div>

        <el-dialog
          v-if="announcement && announcement.title && announcement.active"
          :title="announcement.title"
          :visible.sync="currentInfoVisible"
          :before-close="setMessageRead"
          custom-class="el-dialog--small"
        >
          <div v-html="announcement.text" />
          <span slot="footer" class="dialog-footer">
            <el-button type="tertiary" @click="setMessageRead">
              {{ $t('system.understood') }}
            </el-button>
          </span>
        </el-dialog>

        <TheFooter />
      </div>
    </transition>

    <transition name="offcanvas">
      <TheOffcanvas v-show="offcanvasOpen" />
    </transition>

    <ChatIdleConfirm />
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import deLocale from 'date-fns/locale/de'

import { mapGetters } from 'vuex'

import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import TheFooter from 'molecules/TheFooter/TheFooter'
import TheHeader from 'molecules/TheHeader/TheHeader'
import TheSidebar from 'molecules/TheSidebar/TheSidebar'
import TheOffcanvas from 'molecules/TheOffcanvas/TheOffcanvas'
import ChatIdleConfirm from 'organisms/ChatIdleConfirm/ChatIdleConfirm'
import IconChat from 'assets/icons/chat-solid.svg'
import IconMailsupport from 'assets/icons/mailsupport.svg'
import IconWarningTriangle from 'assets/icons/icon_error_warning_triangle.svg'
import api from 'api'

export default {
  components: {
    IconChat,
    IconMailsupport,
    ChatIdleConfirm,
    TheFooter,
    TheHeader,
    TheSidebar,
    TheOffcanvas,
    VuePerfectScrollbar,
    IconWarningTriangle
  },

  data: () => ({
    currentInfoVisible: false,
    loading: false,
    extrafeedback: require(`assets/bitmaps/extra-feedback.png`)
  }),

  computed: {
    ...mapGetters([
      'announcement',
      'chatIsOpen',
      'currentChatEvent',
      'userInfo',
      'offcanvasOpen',
      'upcomingChatEvents',
      'abandonedChatMessageOpen'
    ]),
    mailText() {
      return window.landingTexts.email
    },
    chatText() {
      return this.chatIsOpen
        ? window.landingTexts.chatOpen +
            '<br>Thema: ' +
            ` ${this.currentChatEvent.subject}`
        : window.landingTexts.chatClosed
    },
    wrapperClasses() {
      let classes = {
        'e-overall--loggedin': this.userInfo && this.userInfo.id
      }
      return classes
    },
    kidsAbandonedInfoText() {
      if (!window.landingTexts) {
        return ""
      }
      return window.landingTexts.kidsAbandonedInfoText
    }
  },
  watch: {
    abandonedChatMessageOpen: {
      handler: function(newVal) {
        if (newVal) {
          api.call('abandonedChatShown')
        } 
      },
      immediate: true
    }
  },
  mounted() {
    if (!this.$cookie.get('ngk_kids_messageRead')) {
      this.currentInfoVisible = true
    }
  },

  methods: {
    setMessageRead() {
      this.$cookie.set('ngk_kids_messageRead', true, 1)
      this.currentInfoVisible = false
    },
    format(date, token) {
      return format(parseISO(date), token, {
        locale: deLocale
      })
    },
    openExternalQuestionnaire() {
      window.open('https://survey.lamapoll.de/NgK-Onlineberatung-Logged-1', '_blank');
    }
  }
}
</script>

<style lang="scss" scoped>
@import '_assets/LoginLanding';
</style>
